/**
 * Guest unified stream-health recorder.
 *
 * Turns the guest's single-pass stats sample plus session lifecycle hooks into the unified
 * per-session health record defined in ./streamHealthContracts: a latest-per-kind snapshot
 * map (video, audio, input, signaling) and a bounded, ordered event timeline. The recorder
 * consumes the SAME GuestStatsSample the congestion monitor already produces — it never
 * triggers a second getStats() poll on a mobile radio.
 *
 * Measurement philosophy (matches the sampler): a field the engine cannot measure is OMITTED
 * from the snapshot, never zero-filled.
 */

import {
  STREAM_HEALTH_MESSAGE_TYPE,
  STREAM_HEALTH_SCHEMA_VERSION,
  type StreamHealthEvent,
  type StreamHealthEventKind,
  type StreamHealthMessage,
  type StreamHealthSnapshot,
  type StreamKind,
} from './streamHealthContracts';
import type { GuestStatsSample } from './guestStreamStatsSampler';

/** Default cap on the retained event timeline (long sessions stay bounded). */
const DEFAULT_MAX_EVENTS = 600;

export class GuestStreamHealthRecorder {
  private readonly maxEvents: number;
  private readonly nowMs: () => number;
  private readonly latestByKind = new Map<StreamKind, StreamHealthSnapshot>();
  private readonly timeline: StreamHealthEvent[] = [];

  constructor(opts?: { maxEvents?: number; nowMs?: () => number }) {
    this.maxEvents = opts?.maxEvents ?? DEFAULT_MAX_EVENTS;
    this.nowMs = opts?.nowMs ?? (() => Date.now());
  }

  /**
   * Ingest one sampler tick: refresh the video (and audio, when present) snapshots and append
   * a 'sample' event. The sample carries the guest's measured wire/congestion shapes; the
   * snapshot reads the same fields so the health record and the congestion ladder never
   * disagree about what was measured.
   */
  observeSample(sample: GuestStatsSample, nowMs?: number): void {
    const atMs = nowMs ?? this.nowMs();
    const wire = sample.wire;

    const video: StreamHealthSnapshot = {
      kind: 'video',
      atMs,
      ...(wire.rttMs !== undefined ? { rttMs: wire.rttMs } : {}),
      ...(wire.packetsLostPct !== undefined ? { packetsLostPct: wire.packetsLostPct } : {}),
      ...(wire.decodeFps !== undefined ? { fps: wire.decodeFps } : {}),
      ...(wire.decodeBacklogFrames !== undefined ? { stallScore: wire.decodeBacklogFrames } : {}),
    };
    this.latestByKind.set('video', video);

    // Audio is observability-only: read what the sampler captured, omit when unmeasured.
    if (wire.audioBitrateBps !== undefined || wire.audioPacketsLostPct !== undefined) {
      const audio: StreamHealthSnapshot = {
        kind: 'audio',
        atMs,
        ...(wire.audioBitrateBps !== undefined ? { bitrateBps: wire.audioBitrateBps } : {}),
        ...(wire.audioPacketsLostPct !== undefined ? { packetsLostPct: wire.audioPacketsLostPct } : {}),
        ...(wire.audioLevel !== undefined ? { stallScore: wire.audioLevel } : {}),
      };
      this.latestByKind.set('audio', audio);
    }

    this.push({ kind: 'sample', stream: 'video', atMs, snapshot: video });
  }

  /** Record a lifecycle/activity event (transport lost/resumed, session end, input). */
  recordEvent(
    kind: StreamHealthEventKind,
    stream: StreamKind,
    detail?: Record<string, number | string | boolean>,
  ): void {
    const atMs = this.nowMs();
    this.push({ kind, stream, atMs, ...(detail ? { detail } : {}) });

    // Keep the non-video kinds' snapshots fresh from lifecycle/activity signals.
    if (stream === 'input' || stream === 'signaling') {
      this.latestByKind.set(stream, { kind: stream, atMs });
    }
  }

  /** Latest snapshot per stream kind, in stable kind order. */
  snapshots(): StreamHealthSnapshot[] {
    const order: StreamKind[] = ['video', 'audio', 'input', 'signaling'];
    const out: StreamHealthSnapshot[] = [];
    for (const kind of order) {
      const snap = this.latestByKind.get(kind);
      if (snap) out.push(snap);
    }
    return out;
  }

  /** Build the wire message for this peer from the latest per-kind snapshots. */
  toWireMessage(peerId: string): StreamHealthMessage {
    return {
      type: STREAM_HEALTH_MESSAGE_TYPE,
      version: STREAM_HEALTH_SCHEMA_VERSION,
      peerId,
      snapshots: this.snapshots(),
    };
  }

  /** The bounded, ordered event timeline. */
  events(): readonly StreamHealthEvent[] {
    return this.timeline;
  }

  reset(): void {
    this.latestByKind.clear();
    this.timeline.length = 0;
  }

  private push(event: StreamHealthEvent): void {
    this.timeline.push(event);
    if (this.timeline.length > this.maxEvents) {
      this.timeline.splice(0, this.timeline.length - this.maxEvents);
    }
  }
}
